<script lang="ts" setup>
import HeartCrackIcon from "@/components/icon/HeartCrackIcon.vue";
import { MIN_CRYSTAL_LIMIT } from "@/components/mobile/chat/constants";
import i18n from "@/locales";
import { copyToClipboard } from "@/modules/common";
import { useChat } from "@/stores/chat";
import { $bus } from "@/stores/eventBus";
import { useGlobalUser } from "@/stores/globalUser";
import { IChatMessageItem } from "@/types/chat";
import { useWindowSize } from "@vueuse/core";
import {
  Cell,
  CellGroup,
  Popup,
  showConfirmDialog,
  showLoadingToast,
  showSuccessToast,
} from "vant";
import { computed, ref } from "vue";

const chat = useChat();
const user = useGlobalUser();
const bubbleActionsShow = ref(false);
const showMsgEditPopup = ref(false);
const targetMessage = ref<IChatMessageItem | null>(null);
const isPrivate = ref(false);
const sessionId = ref(0);
const msgEditFieldValue = ref("");
const isNsfw = computed(() => targetMessage.value?.isNsfw === 1);
const { height } = useWindowSize();

async function onDeleteMsg() {
  await showConfirmDialog({
    title: i18n.global.t("ConfirmModal.deleteMessage"),
    message: i18n.global.t("ConfirmModal.deleteMessageText"),
    confirmButtonText: i18n.global.t("Buttons.confirm"),
    cancelButtonText: i18n.global.t("Buttons.cancel"),
  });
  const loading = showLoadingToast({
    message: i18n.global.t("Message.deleting"),
  });
  try {
    await chat.deleteChatMessage(sessionId.value, targetMessage.value!.id!);
    showSuccessToast(i18n.global.t("Message.deleteSuccessful"));
    bubbleActionsShow.value = false;
  } finally {
    loading.close();
  }
}

const onCopy = async () => {
  copyToClipboard(targetMessage.value!.msg!);
  showSuccessToast(i18n.global.t("Message.copySuccessful"));
  bubbleActionsShow.value = false;
};

const markAsDislike = async () => {
  await chat.markAsDislike(targetMessage.value!.id!, sessionId.value);
  bubbleActionsShow.value = false;
};

async function onConfirmMsgEdit() {
  // 1. 检查是否需要消耗水晶
  const needCrystal = targetMessage.value!.isNsfw;
  if (needCrystal) {
    if (user.user.crystalQuantity < MIN_CRYSTAL_LIMIT) {
      chat.showInsufficientCrystalPopup = true;
      return;
    }
  }
  // 2. 修改消息
  await showConfirmDialog({
    title: i18n.global.t("ConfirmModal.modifyMessage"),
    message: i18n.global.t("ConfirmModal.modifyMessageText"),
    confirmButtonText: i18n.global.t("Buttons.confirm"),
    cancelButtonText: i18n.global.t("Buttons.cancel"),
  });
  const loading = showLoadingToast({
    message: i18n.global.t("Message.modifying"),
  });
  try {
    await chat.updateChatMessage(targetMessage.value!.id!, {
      text: msgEditFieldValue.value,
    });
    if (needCrystal) {
      await user.refreshUser();
    }
    loading.close();
    showSuccessToast(i18n.global.t("Message.successfully"));
    showMsgEditPopup.value = false;
    bubbleActionsShow.value = false;
  } finally {
    loading.close();
  }
}

const onEdit = () => {
  showMsgEditPopup.value = true;
  msgEditFieldValue.value = targetMessage.value?.msg ?? "";
};

const onRegenerate = () => {
  chat.sessionId2MessageList[sessionId.value].keyList.shift();
  bubbleActionsShow.value = false;
  $bus.$emit("chat-message-regenerate");
};

const showRegenerate = ref(false);
const showBubbleActions = (
  msg: IChatMessageItem,
  mateSessionId: number,
  isPrivateMode: boolean,
  _showRegenerate = false
) => {
  bubbleActionsShow.value = true;
  targetMessage.value = msg;
  isPrivate.value = isPrivateMode;
  sessionId.value = mateSessionId;
  showRegenerate.value = _showRegenerate;
};

const editMsg = (msg: IChatMessageItem, mateSessionId: number) => {
  targetMessage.value = msg;
  sessionId.value = mateSessionId;
  onEdit();
};

export interface BubbleActionPopupInstance {
  showBubbleActions: typeof showBubbleActions;
  editMsg: typeof editMsg;
}

defineExpose({
  showBubbleActions,
  editMsg,
});
</script>
<template>
  <popup
    v-model:show="bubbleActionsShow"
    class="py-4 flex flex-col gap-2"
    position="bottom"
    round
    teleport="body"
  >
    <cell-group inset>
      <cell
        v-if="!isPrivate && !targetMessage!.reactions?.includes('dislike')"
        :value="$t('Cell.willReduceMessage')"
        clickable
        @click="markAsDislike"
      >
        <template #title>
          <div class="flex items-center gap-2">
            <heart-crack-icon class="icon-sm text-base-content" />
            <span>{{ $t("Texts.notLike") }}</span>
          </div>
        </template>
      </cell>
      <cell
        v-if="isPrivate"
        :value="$t('Cell.himWillAlsoMessage')"
        clickable
        @click="onDeleteMsg"
      >
        <template #title>
          <div class="flex items-center gap-2">
            <i class="iconify mingcute--delete-2-line" />
            <span>{{ $t("Buttons.delete") }}</span>
          </div>
        </template>
      </cell>
      <cell
        v-if="isPrivate"
        :value="$t('Cell.himMemoryWillModified')"
        clickable
        @click="onEdit"
      >
        <template #title>
          <div class="flex items-center gap-2">
            <i class="iconify mingcute--edit-line" />
            <span>{{ $t("Buttons.update") }}</span>
          </div>
        </template>
      </cell>
      <cell v-if="isPrivate && showRegenerate" clickable @click="onRegenerate">
        <template #title>
          <div class="flex items-center gap-2">
            <i class="iconfont icon-refresh w-[21px]" />
            <span>{{ $t("Buttons.regenerate") }}</span>
          </div>
        </template>
      </cell>
      <cell clickable @click="onCopy">
        <template #title>
          <div class="flex items-center gap-2">
            <i class="iconify mingcute--copy-2-line" />
            <span>{{ $t("Buttons.copyText") }}</span>
          </div>
        </template>
      </cell>
    </cell-group>
    <cell-group inset>
      <cell
        :title="$t('Buttons.cancel')"
        class="text-base-content/50"
        clickable
        @click="bubbleActionsShow = false"
      />
    </cell-group>
  </popup>
  <popup
    v-model:show="showMsgEditPopup"
    :close-on-click-overlay="false"
    class="grid grid-rows-[auto_1fr] h-[80%] overflow-hidden"
    position="bottom"
    round
    teleport="body"
  >
    <div class="flex items-center justify-between p-2">
      <button class="btn btn-sm btn-ghost" @click="showMsgEditPopup = false">
        {{ $t("Buttons.cancel") }}
      </button>
      <button
        :class="{
          'bg-love': isNsfw,
        }"
        :disabled="!msgEditFieldValue"
        class="btn btn-sm"
        @click="onConfirmMsgEdit"
      >
        {{ $t("Buttons.confirm") }}
        <span v-if="isNsfw" class="flex items-center">
          <CrystalIcon />
          0.2
        </span>
      </button>
    </div>
    <textarea
      v-model="msgEditFieldValue"
      class="textarea w-full h-full focus:outline-0 text-xs lg:text-sm border-0 border-t rounded-none"
    />
  </popup>
</template>
